import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './icons.js';
import { useConfirm } from './confirm.js';
import { useHistoryDismiss } from './use-history-dismiss.js';
import { useDismissOnOutsideClick } from '../platform/dismiss-on-outside-click.js';
import { trapTab } from '../platform/trap-tab.js';

/**
 * The searchable option sheet — design v1.1 §4.2/§4.3, one component with two modes.
 * This is where two documented Cheqroom failures go to die:
 *   P3 scroll-only dropdowns  → the search input filters as typed; prefix matches first
 *   P1 mobile multi-select    → multi mode is plain <button> toggles with a running
 *                               count; no <select multiple>, no library touch model
 *
 * Recents pin to the top of the unfiltered list, per sheet title, session-lifetime —
 * the atelier picks the same colours all afternoon; yesterday is what sticky context
 * and defaults are for, not this.
 */

/** The add row's optional number, as the API wants it: blank means "no standard", which is
 *  a real answer and not a missing one. A non-numeric or out-of-range entry also becomes
 *  null rather than a guess — the server refuses out-of-range with a sentence. */
function parsedExtra(raw: string): number | null {
  const n = Number(raw.trim());
  return raw.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 1825 ? n : null;
}

/** Where the picker was opened from, in viewport coordinates. `null` centres it. */
export type AnchorRect = { top: number; bottom: number; left: number; width: number };

/** Capture the trigger's position at the click. Callers store this in their sheet state,
 * so the picker can open ON the control the user just pressed rather than over the page. */
export function anchorOf(event: { currentTarget: unknown }): AnchorRect | null {
  const el = event.currentTarget;
  if (!(el instanceof HTMLElement)) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, width: r.width };
}

/**
 * MANAGING THE LIST FROM INSIDE THE LIST — maintainer request, 30 Jul 2026: "when I hover
 * over an option there needs to be a pencil icon that appears next to it."
 *
 * Categories and locations were only editable on the Structure screen. But the moment you
 * discover a location is misnamed is the moment you are looking at it in this dropdown, and
 * there are 55 categories — managing them through a chip on the item would be worse than the
 * screen it replaces. So the management lives on the rows themselves.
 *
 * Each callback returns an ERROR MESSAGE or null, rather than throwing or returning a
 * boolean: the caller owns the API call and the server owns the sentence, and a refusal for
 * a non-admin has to reach the user as words rather than as a failed state.
 */
export interface OptionManage {
  /** Singular, lower case: "category", "location". Used in the add row and the confirms. */
  noun: string;
  /** Blast radius for the delete confirm, e.g. "12 items will lose their category". The
   * caller reads it from `structureUsage`; a missing entry must say so, never imply zero. */
  radiusFor: (value: string) => string;
  /**
   * THE WHOLE DELETE-CONFIRM BODY, when the generic "<radius>. This cannot be undone from the app."
   * says the wrong thing for this list (0.1.528). Sources are the first: since 093 deleting one
   * DETACHES it from its boxes instead of refusing, and the person needs to hear that the boxes stay
   * ("It will be removed from 3 boxes. The boxes stay, with no source."), not a radius that reads
   * like the boxes go with it. Absent = the generic sentence, exactly as before.
   */
  deleteBodyFor?: ((value: string) => string) | undefined;
  rename: (value: string, next: string, extra?: number | null) => Promise<string | null>;
  remove: (value: string) => Promise<string | null>;
  /**
   * `parentPath` is `undefined` for a top-level item and for lists that have no hierarchy
   * at all. It is threaded through rather than inferred: guessing at the currently-selected
   * value's parent would be a default that looks like a decision.
   */
  add: (name: string, parentPath: string | undefined, extra?: number | null) => Promise<string | null>;
  /** Present = the add row offers a parent. Absent = the list is flat (locations). */
  parentOptions?: string[] | undefined;
  /**
   * An optional SECOND field on the add row, for a list whose new entries carry one more
   * fact than a name. The source picker uses it for a supplier's standard return window
   * (maintainer, 5 Aug 2026: "you should both be able to give the source a name and the
   * maximum amount of days for return").
   *
   * Numeric and OPTIONAL by construction — a source with no standard window is a real
   * answer (a designer loan is negotiated per piece), so an empty box means `null` rather
   * than a validation error. Additive: every existing `manage` ignores it, and `add`
   * receives it as a third argument that current implementations do not declare.
   */
  extraNumber?:
    | {
        label: string;
        placeholder?: string;
        /**
         * The value an EXISTING option already carries, so the pencil opens showing it
         * rather than blank. Absent (or `null`) renders empty, which means "no standard" —
         * and an empty box on save means the same thing, so clearing is expressible.
         */
        valueFor?: (option: string) => number | null;
      }
    | undefined;
  /**
   * THE LIST ITSELF, not its values — renaming or deleting the FIELD this dropdown
   * belongs to (maintainer, 31 Jul 2026: "make it so the pencil also enables renaming and
   * deleting the field just like the options within the field").
   *
   * It lives INSIDE pencil mode, rather than in a footer that was always on screen. Same
   * gesture as everything else the pencil governs, and it stops two destructive buttons
   * sitting under a picker that most people only ever pick from.
   *
   * ⚠️ IT RENDERS IN THE HEADER, AS THE TITLE (moved there 1 Aug 2026). The title of the
   * dropdown IS the name of this thing, so editing it anywhere else made a rename look like
   * a different feature from the option renames directly above it. In pencil mode the
   * header becomes input + Save + ✕ — the same three controls, in the same order, as every
   * option row.
   *
   * ⚠️ THE SCOPES ARE GENUINELY DIFFERENT. In the list, deleting a row removes a
   * SUGGESTION and every stored value survives. Here, deleting removes the field and every
   * value on every item with it. That is why this carries its own `radius` rather than
   * reusing `radiusFor`, and why its confirm names a destructive count.
   */
  self?:
    | {
        /** Singular, lower case: "field". Used in the confirm and the labels. */
        noun: string;
        /** Its current name, which is also the starting draft. */
        label: string;
        /** What deleting it costs, e.g. "12 values on 9 items are deleted with it".
         * Unused when `remove` is absent — there is nothing to warn about. */
        radius?: string | undefined;
        rename: (next: string) => Promise<string | null>;
        /**
         * ⚠️ OPTIONAL SINCE 1 Aug 2026, AND ABSENCE IS A REAL STATE. A user-defined field
         * can be renamed AND deleted. The three BUILT-IN headings — Brand, Category,
         * Location — can only be renamed: `items.brand` and the `categories` table exist
         * whatever anyone calls them, so there is nothing to delete.
         *
         * When it is absent the ✕ is not rendered at all, rather than rendered and
         * refusing. An act that must always fail is a worse answer than no act — the same
         * ruling the item screen makes about offering check-out on a flagged garment.
         */
        remove?: (() => Promise<string | null>) | undefined;
      }
    | undefined;
}

export interface OptionSheetProps {
  title: string;
  options: string[];
  selected: string[];
  multi: boolean;
  /** Offer "use ⟨typed⟩" when nothing matches — brand is free text (708 known, more arrive). */
  allowFree?: boolean;
  onChange: (values: string[]) => void;
  onClose: () => void;
  /** Absent or null = centred. See `anchorOf`. */
  anchor?: AnchorRect | null;
  /** Absent = a plain picker. Present = rows gain rename/delete and an add row. */
  manage?: OptionManage | undefined;
  /**
   * FULL-BLEED, FOR STAGED FLOWS ONLY (maintainer, 1 Aug 2026).
   *
   * ⚠️ THIS LOOKS LIKE A REVERSAL OF 0.1.71 AND IS NOT. That release made the picker a
   * small anchored dropdown because a whole-screen takeover to choose one value was
   * "very overwhelming" — and that judgement stands everywhere a picker is opened FROM A
   * CONTROL ON A SCREEN YOU ARE READING. `.picker-centred`'s own comment still says
   * "never back to full-bleed", and it is still right about that case.
   *
   * The bulk-edit flow is the case it was not about. There the picker is not overlaying a
   * screen — the screen it would overlay is itself a full-bleed `.sheet` (Edit N items),
   * so a centred card floating on top of a full-screen sheet is the inconsistency, not
   * the fix: "it should open a full screen view to keep consistent with the edit items
   * screen, which is also a full screen view."
   *
   * So this is opt-in, per call site, and the default is unchanged. Anchored and centred
   * behave exactly as they did.
   */
  fullscreen?: boolean;
  /**
   * Content pinned BELOW the option list, above the Close/Done bar (0.1.362). For a picker
   * that carries one more control than a list of options — the outfit-number stepper on the
   * character sheet, chosen alongside the character. `flex: none`, so the list still scrolls
   * above it (the WebKit collapse fix on `.sheet-options` is untouched). Additive: absent =
   * no footer region, every existing call site unchanged.
   */
  footer?: ReactNode;
  /**
   * SINGLE-SELECT THAT DOES NOT CLOSE ON PICK (0.1.362). Default single-select commits and
   * closes in one tap; with this on it commits (fires `onChange`) but stays open, so a
   * following control in `footer` (the outfit number) is reachable after the pick. Dismissal
   * is then the Close bar / backdrop / Escape. Ignored in multi mode (which never auto-closes).
   */
  keepOpenOnSelect?: boolean;
}

/** Session-lifetime recents, keyed by sheet title. Module state on purpose: survives
 * navigation, dies with the tab, needs no storage schema. */
const recents = new Map<string, string[]>();

function noteRecent(title: string, value: string): void {
  const list = recents.get(title) ?? [];
  recents.set(title, [value, ...list.filter((v) => v !== value)].slice(0, 5));
}

import { isColourField, needsOutline, swatchFor } from '../platform/colour.js';

export function OptionSheet({
  title,
  options,
  selected,
  multi,
  allowFree = false,
  onChange,
  onClose,
  anchor = null,
  manage,
  fullscreen = false,
  footer,
  keepOpenOnSelect = false,
}: OptionSheetProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>(selected);
  /**
   * RE-SEED `picked` WHEN THE SELECTED VALUES CHANGE (0.1.348) — the fix for the rename
   * DUPLICATION ghost. `picked` is seeded from `selected` once; a Manage rename/delete then
   * re-reads the record (the manage `reload`) and the parent passes the NEW spelling in `selected`,
   * but `picked` kept the OLD one — which is no longer in `options`, so `freeSelected` below rendered
   * it as a lingering row (the renamed value appearing to "stick around" until the sheet closed).
   * ⚠️ KEYED ON THE JOINED VALUES, not the array identity: callers rebuild `[box.projectId]` fresh
   * every render, so depending on `selected` itself would reset a multi-draft on every frame. The
   * key only changes when the actual values do — a parent commit or a manage reload — which is
   * exactly when `picked` must follow. */
  const selectedKey = selected.join(' ');
  useEffect(() => {
    setPicked(selected);
    // selectedKey is the value-fingerprint of `selected`; `selected` itself is intentionally omitted.
  }, [selectedKey]);
  // The delete confirms below open a Confirm sheet OVER this picker (it is `elevated`, z 20).
  const [confirmEl, ask] = useConfirm();
  // Back / edge-swipe closes the picker instead of popping the route (S2 part 3).
  useHistoryDismiss(onClose);
  /** Computed once per sheet, not per row: the title does not change while it is open. */
  const colourField = isColourField(title);
  const input = useRef<HTMLInputElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  // Focus lands on the PANEL on open (0.1.507) so Tab starts inside it and `trapTab` keeps it
  // there — not on the search box, which would raise the keyboard (the 30 Jul rule).
  useEffect(() => {
    panel.current?.focus();
  }, []);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);
  /** Management state. `editing` is the option whose row has turned into a rename input;
   * `addingNew` is the draft in the add row; `manageProblem` is the server's own sentence. */
  /** The pencil is a MODE: it turns every row editable at once, rather than one at a time. */
  const [editingAll, setEditingAll] = useState(false);
  /** Drafts while editing, keyed by current value. Absent = untouched. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [addingNew, setAddingNew] = useState<string | null>(null);
  const [addingExtra, setAddingExtra] = useState('');
  /**
   * THE MANAGE MENU (redesign 5b). The header's pencil (edit names) and plus (add) folded
   * into one "Manage…" button that opens this menu. They stay TWO ACTIONS behind it, not one
   * mode, because they need opposite row renderings: adding leaves rows as selectable buttons
   * (so a freshly-added option can be picked in the same breath — inline-structure.spec's
   * "pickable in the same breath"), while editing names turns every row into an input.
   */
  const [manageMenuOpen, setManageMenuOpen] = useState(false);
  /** Per-row drafts for the extra field while the pencil is open, keyed by option. */
  const [extraDrafts, setExtraDrafts] = useState<Record<string, string>>({});

  /**
   * ⚠️ CALLED WITH TWO ARGUMENTS WHEN THERE IS NO EXTRA FIELD, and that is not cosmetic.
   * Passing a third `undefined` unconditionally changes the observed call shape for every
   * list that has no extra field — brand, category, location, field values — which is
   * exactly what `option-sheet.test.tsx` asserts about (`add('Rack C', undefined)`). Those
   * assertions are right: a picker with no second field should not be calling a
   * three-argument API. The arity is part of the contract.
   */
  /**
   * ⚠️ TWO ARGUMENTS WHEN THERE IS NO EXTRA FIELD — same contract as `runAdd` below, and
   * for the same reason: passing a third `undefined` would change the observed call shape
   * for every picker that has no second field, which `option-sheet.test.tsx` asserts about.
   */
  const runRename = (option: string, next: string): Promise<string | null> => {
    if (manage === undefined) return Promise.resolve(null);
    if (manage.extraNumber === undefined) return manage.rename(option, next);
    const draft = extraDrafts[option];
    return manage.rename(option, next, draft === undefined ? null : parsedExtra(draft));
  };

  const runAdd = (name: string): Promise<string | null> => {
    if (manage === undefined) return Promise.resolve(null);
    const parent = addParent === '' ? undefined : addParent;
    return manage.extraNumber === undefined
      ? manage.add(name, parent)
      : manage.add(name, parent, parsedExtra(addingExtra));
  };
  /** The draft name for the list ITSELF, kept apart from the per-option drafts because it
   *  is a different scope and shares none of their keys. */
  const [selfDraft, setSelfDraft] = useState<string | null>(null);
  /** '' means top level — a real choice, not "unset". */
  const [addParent, setAddParent] = useState('');
  const [manageProblem, setManageProblem] = useState<string | null>(null);
  const [manageBusy, setManageBusy] = useState(false);

  /**
   * The Manage… menu is dismissed by the next click anywhere (no backdrop of its own; the
   * picker's backdrop closes the whole picker).
   *
   * ⚠️ THIS WAS THE SECOND COPY of the listener that closed a menu with its own opening click —
   * unreported only because Manage… is reached less often than the maintenance row's ⋯. Fixed in
   * the same pass, through the shared hook, so there is one place left to get it wrong.
   */
  useDismissOnOutsideClick(manageMenuOpen, () => setManageMenuOpen(false));

  const runManage = async (fn: () => Promise<string | null>): Promise<boolean> => {
    if (manageBusy) return false;
    setManageBusy(true);
    setManageProblem(null);
    const message = await fn();
    setManageBusy(false);
    if (message !== null) {
      setManageProblem(message);
      return false;
    }
    return true;
  };

  /**
   * NO AUTOFOCUS ON OPEN (30 Jul 2026). It used to focus the search box, which on a
   * phone raises the keyboard over the very list you opened: "mijn klavier popt
   * onmiddellijk open in de type-to-filter… dan moet ik altijd back doen op mijn
   * klavier, om dat klavier weg te doen, en dan scrollen."
   *
   * The reasoning behind it — the pre-populated list should answer ~90% of cases — is
   * exactly the argument AGAINST focusing: if the value is nearly always already in the
   * list, SCROLLING is the primary gesture and typing is the fallback. Focusing put the
   * fallback in the way of the default. Type-to-filter is untouched and one tap away.
   *
   * Deliberately not `focus({ preventScroll: true })` or a blur-after-focus dance:
   * neither reliably stops a mobile keyboard, and a half-working trick here reads as a
   * flicker. The honest fix is not to take focus.
   */

  /**
   * ANCHORED, ON EVERY SIZE OF SCREEN — maintainer decision, 30 Jul 2026.
   *
   * This used to be `position: fixed; inset: 0`: a whole-screen takeover for choosing one
   * value. Field report: "it opens a full screen option list which is very overwhelming
   * and throws me off." The full-screen form was a deliberate mobile-first choice and it
   * was not wrong for a phone, but it was asked for as a small dropdown on BOTH, so both
   * is what this is — no viewport branch, one behaviour to reason about.
   *
   * Measured AFTER render, not guessed: the panel's real height decides whether it opens
   * downward or flips above the trigger, and the left edge is clamped so a control near
   * the right edge does not push the list off-screen. Guessing a height here is how a
   * dropdown ends up half below the fold on exactly the one field with two options.
   */
  /**
   * DRAGGING, BY THE TITLE (maintainer, 31 Jul 2026: "i want to be able to drag the
   * dropdowns around the screen while holding it at the title and next to the title").
   *
   * The reason is the anchor: a dropdown opens under the control that opened it, so on a
   * phone it frequently covers the very field you are choosing FOR — you cannot see what
   * you are setting while you set it. Moving it is quicker than closing, scrolling and
   * reopening.
   *
   * `dragged` is an OFFSET from the placement, not an absolute position. Keeping the two
   * apart means the auto-placement above still runs on scroll and resize — a dragged panel
   * that stopped following the viewport would sail off the edge on the next scroll, and one
   * that forgot the drag would snap back mid-gesture.
   */
  const [dragged, setDragged] = useState<{ x: number; y: number } | null>(null);
  const dragFrom = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const dragStart = (e: React.PointerEvent<HTMLElement>): void => {
    // Only the handle itself, never a control sitting on it: the tools live in this header
    // and a press on the pencil must stay a press on the pencil. `input` joined the list on
    // 1 Aug 2026 when the title became an editable field in pencil mode — without it, the
    // gesture that places a caret in the name would instead drag the whole panel, and the
    // text would be unselectable by pointer.
    if ((e.target as HTMLElement).closest('button, input') !== null) return;
    if (anchor === null || fullscreen) return;
    dragFrom.current = { x: e.clientX, y: e.clientY, ox: dragged?.x ?? 0, oy: dragged?.y ?? 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const dragMove = (e: React.PointerEvent<HTMLElement>): void => {
    const from = dragFrom.current;
    if (from === null) return;
    // Prevents the page scrolling under the finger mid-drag on a touch screen.
    e.preventDefault();
    setDragged({ x: from.ox + (e.clientX - from.x), y: from.oy + (e.clientY - from.y) });
  };

  const dragEnd = (e: React.PointerEvent<HTMLElement>): void => {
    dragFrom.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  useLayoutEffect(() => {
    if (anchor === null) {
      setPlacement(null);
      return;
    }
    const place = (): void => {
      const node = panel.current;
      if (!node) return;
      const height = node.offsetHeight;
      const width = node.offsetWidth;
      const margin = 8;
      const below = anchor.bottom + 4;
      const fitsBelow = below + height <= window.innerHeight - margin;
      // Flip above only if there is genuinely more room there; otherwise stay below and
      // let it clamp, because a panel pinned to the top edge over its own trigger is
      // worse than one that scrolls.
      const top = fitsBelow
        ? below
        : Math.max(margin, Math.min(anchor.top - height - 4, window.innerHeight - height - margin));
      const left = Math.max(margin, Math.min(anchor.left, window.innerWidth - width - margin));
      /**
       * THE DRAG OFFSET IS CLAMPED HERE, not while dragging. Clamping the gesture makes the
       * panel stick to the edge and fight the finger; clamping the RESULT lets it be
       * dragged anywhere and still never end up somewhere it cannot be read from. The
       * header is kept on screen rather than the whole panel — a tall dropdown is meant to
       * hang below the fold, but the bar you grab it by must stay catchable.
       */
      const dx = dragged?.x ?? 0;
      const dy = dragged?.y ?? 0;
      setPlacement({
        top: Math.max(margin, Math.min(top + dy, window.innerHeight - 48)),
        left: Math.max(margin - width + 64, Math.min(left + dx, window.innerWidth - 64)),
      });
    };
    place();
    // Reposition rather than close: a stray scroll while reading the options should not
    // throw away a half-made multi-selection.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor, dragged]);

  const shown = useMemo(() => {
    // Free-entered values live in `picked` but not in `options`; they render first so a
    // just-added value is visibly selected rather than vanishing into nowhere.
    // ⚠️ CASE-INSENSITIVE match against options (0.1.348): a stored value whose spelling differs
    // only in case from the folded display option (e.g. picked "case-a", option "Case-a") must NOT
    // render as a second free row — the vocab reads fold on lower(btrim(name)), so the two are one.
    const norm = (s: string): string => s.trim().toLowerCase();
    const freeSelected = picked.filter((p) => !options.some((o) => norm(o) === norm(p)));
    const q = query.trim().toLowerCase();
    if (q === '') {
      const pinned = (recents.get(title) ?? []).filter((r) => options.includes(r));
      return [...freeSelected, ...pinned, ...options.filter((o) => !pinned.includes(o))];
    }
    const prefix = options.filter((o) => o.toLowerCase().startsWith(q));
    const infix = options.filter((o) => !o.toLowerCase().startsWith(q) && o.toLowerCase().includes(q));
    return [...freeSelected.filter((v) => v.toLowerCase().includes(q)), ...prefix, ...infix];
  }, [query, options, title, picked]);

  const freeValue = query.trim();
  const offerFree =
    allowFree && freeValue !== '' && !options.some((o) => o.toLowerCase() === freeValue.toLowerCase());

  const chooseSingle = (value: string): void => {
    noteRecent(title, value);
    onChange([value]);
    // keepOpenOnSelect: commit but stay open, so a footer control (the outfit number) is
    // reachable after picking. Dismissal is the Close bar / backdrop / Escape.
    if (!keepOpenOnSelect) onClose();
  };

  const toggle = (value: string): void => {
    noteRecent(title, value);
    setPicked((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  /** Free entry in MULTI mode adds the value and keeps the sheet open — a new colour is
   * usually one of several, and closing would cost the rest of the selection. The typed
   * value joins `picked` and renders as a selected row; the schema is genuinely open
   * (item_field_values has no FK to options — options are suggestions, not an enum). */
  const chooseFree = (value: string): void => {
    if (multi) {
      noteRecent(title, value);
      setPicked((prev) => (prev.includes(value) ? prev : [...prev, value]));
      setQuery('');
      input.current?.focus();
    } else {
      chooseSingle(value);
    }
  };

  return (
    <>
      {/* Catches the click that lands outside. A dropdown you cannot dismiss by clicking
          away is the complaint this change exists to fix, in a smaller form.

          NOT rendered full-bleed: there is no "outside" to click, so a transparent layer
          under a panel that covers the viewport would be a click target that can never be
          reached — dead weight that the next reader has to work out is dead. The header's
          ✕ and Escape are the ways out there, and both already exist. */}
      {confirmEl}
      {!fullscreen && <div className="picker-backdrop" onClick={onClose} aria-hidden="true" />}
      <div
        ref={panel}
        className={fullscreen ? 'picker picker-fullscreen' : anchor === null ? 'picker picker-centred' : 'picker'}
        role="dialog"
        aria-label={title}
        // Full-bleed has nothing to place: it IS the viewport. Passing a measured top/left
        // would fight `inset: 0` and is the kind of dead style that reads as intent.
        style={
          fullscreen || placement === null
            ? undefined
            : { top: `${String(placement.top)}px`, left: `${String(placement.left)}px` }
        }
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
            return;
          }
          // TAB STAYS INSIDE (0.1.507) — the shared walk, see `platform/trap-tab.ts`.
          trapTab(e, panel.current);
        }}
        // Focusable so the first Tab starts from the panel — NOT the search box: focusing that
        // raises the keyboard, which the 30 Jul rule forbids (intake.spec pins it). A div takes
        // focus without one.
        tabIndex={-1}
      >
      {/* Full-bleed is never draggable — it already fills the screen, so the gesture has
          nowhere to move it to and would only make the panel jitter under the finger. The
          `fullscreen` term is checked here as well as in `dragStart`, so a caller that
          passes an anchor AND fullscreen cannot end up with a draggable viewport. */}
      <header
        className={anchor === null || fullscreen ? 'sheet-header' : 'sheet-header sheet-header-draggable'}
        onPointerDown={dragStart}
        onPointerMove={dragMove}
        onPointerUp={dragEnd}
        onPointerCancel={dragEnd}
      >
        {/**
          * THE TITLE IS THE RENAME FIELD, in pencil mode (maintainer, 1 Aug 2026: "the title
          * should become an editable text field just like every option in the list with a
          * save and cross button next to it").
          *
          * It used to be a row at the BOTTOM of the options, which put the name of the thing
          * in a different place from the thing itself and read as a separate form.
          *
          * ⚠️ ITS SAVE AND DELETE MOVED BELOW THE INPUT (maintainer, 1 Aug 2026: "when I
          * click the pencil, the title section of the drop down gets very crowded … put it
          * under the title text field"). On a phone the bar was holding a text field, Save,
          * a delete ✕, a running count, the pencil, the plus AND the close ✕ — seven
          * controls, and the title input had collapsed to about three characters ("Mat…").
          *
          * The first version put them beside the input to match an option row exactly. That
          * was the right instinct for the SHAPE and the wrong one for the SPACE: an option
          * row is a whole row wide, and the title shares its row with every tool the panel
          * has. Same three controls, wrapped rather than crammed.
          *
          * ⚠️ TWO ✕ IN ONE PANEL. The one below deletes the field; the one in the tools
          * closes the panel. They are now on different ROWS rather than merely apart, which
          * is a stronger separation than the original comment could claim.
          */}
        {manage?.self !== undefined && editingAll ? (
          <>
            <input
              className="sheet-rename sheet-title-rename"
              type="text"
              aria-label={`New name for the ${manage.self.noun} ${manage.self.label}`}
              value={selfDraft ?? manage.self.label}
              onChange={(e) => {
                setSelfDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const next = (selfDraft ?? '').trim();
                if (next !== '' && next !== manage.self?.label) void runManage(() => manage.self!.rename(next));
              }}
            />
          </>
        ) : (
          <strong>{title}</strong>
        )}
        {/**
          * ⚠️ THE COUNT IS HIDDEN IN PENCIL MODE (maintainer, 1 Aug 2026: "the x amount
          * selected text needs to go away to reduce clutter in the editing mode").
          *
          * It is not merely clutter — it is answering a question nobody is asking. Pencil
          * mode is for renaming the list; how many values are picked is a fact about
          * CHOOSING, and the rows underneath are not choosable while they are text fields.
          * It comes straight back on leaving edit mode, and `picked` is untouched
          * throughout, so nothing about the selection changes — only whether it is
          * narrated.
          */}
        {multi && !editingAll && <span role="status">{picked.length} selected</span>}
        {/**
          * ⚠️ THE TOOLS MOVED HERE FROM EVERY ROW (maintainer, 30 Jul 2026). A pencil and a
          * cross beside each value put two destructive targets next to all 55 categories —
          * on a phone the tap meant for "Tops" lands on the cross beside it. One set at the
          * top is one place to look and one place to mis-tap.
          */}
        {/**
          * ⚠️ ONE GROUP FOR ALL THREE ICONS (maintainer, 1 Aug 2026: "there is a gap between
          * the plus icon and the cross … just put the same space between the plus and the
          * cross as the space that is between the pencil and the plus").
          *
          * The close ✕ used to be a SIBLING of this group, so it inherited the header's own
          * `gap: 0.5rem` while the pencil and plus shared the group's `0.15rem`. Three icons
          * that read as one row of tools were spaced two-plus-one, and the odd one out was
          * the one that looks destructive.
          *
          * The group renders whether or not `manage` is present, because a plain picker
          * still has exactly one tool — the close.
          */}
        <span className="sheet-header-tools">
          {/**
            * MANAGE… (redesign 5b). The pencil and plus that used to sit here are two menu
            * items now, behind one text button, so a picker most people only ever PICK from
            * shows one quiet control instead of two bare icons. The two acts stay separate —
            * see the note on `manageMenuOpen` for why they cannot become a single mode.
            * `stopPropagation` on the toggle so the document-level dismiss (added while the
            * menu is open) does not immediately re-close it on this very click.
            */}
          {manage !== undefined && (
            <span className="sheet-manage">
              <button
                type="button"
                className={
                  manageMenuOpen || editingAll || addingNew !== null ? 'sheet-tool sheet-manage-btn sheet-tool-on' : 'sheet-tool sheet-manage-btn'
                }
                aria-haspopup="true"
                aria-expanded={manageMenuOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setManageMenuOpen((open) => !open);
                }}
              >
                Manage…
              </button>
              {manageMenuOpen && (
                <span className="sheet-manage-menu" role="group" aria-label={`Manage ${title}`}>
                  <button
                    type="button"
                    aria-label={`Add a ${manage.noun}`}
                    onClick={() => {
                      setManageMenuOpen(false);
                      // Seeded with whatever was typed to filter: you searched for "Denim",
                      // found nothing, and the thing you want to do next is add it. Leaves
                      // rows SELECTABLE (editingAll false), so the new one can be picked at
                      // once — the flow inline-structure.spec calls "in the same breath".
                      setAddingNew(query.trim());
                      setEditingAll(false);
                    }}
                  >
                    Add a {manage.noun}
                  </button>
                  <button
                    type="button"
                    aria-pressed={editingAll}
                    aria-label={editingAll ? `Stop editing the ${manage.noun} names` : `Edit the ${manage.noun} names`}
                    onClick={() => {
                      setManageMenuOpen(false);
                      // LEAVING EDIT MODE DISCARDS UNSAVED NAMES (maintainer, 30 Jul 2026: "if
                      // I close the pencil state, it should discard any unsaved changes").
                      // Keeping them would mean a half-typed rename reappears next time,
                      // looking like an edit that was saved and was not. `selfDraft` is part
                      // of this reset (found missing 1 Aug 2026 — the title did not obey the
                      // rule the option rows did).
                      setEditingAll(!editingAll);
                      setDrafts({});
                      setAddingNew(null);
                      setSelfDraft(null);
                    }}
                  >
                    {editingAll ? `Stop editing names` : `Edit the ${manage.noun} names`}
                  </button>
                </span>
              )}
            </span>
          )}
          {/* A cross, not the word "Cancel": it closes rather than undoing anything, since
              nothing here is staged. */}
          <button type="button" className="sheet-tool" aria-label={`Close ${title}`} onClick={onClose}>
            <Icon name="cross" />
          </button>
        </span>
      </header>
      {/**
        * THE TITLE'S OWN ACTIONS, on their own row. See the note in the header: an option
        * row is a whole row wide and the title is not, so the same three controls wrap
        * instead of competing with every tool the panel has.
        */}
      {manage?.self !== undefined && editingAll && (
        <p className="sheet-title-actions">
          <button
            type="button"
            disabled={manageBusy || (selfDraft ?? manage.self.label).trim() === manage.self.label}
            onClick={() => {
              const next = (selfDraft ?? '').trim();
              if (next !== '' && next !== manage.self?.label) void runManage(() => manage.self!.rename(next));
            }}
          >
            Save
          </button>
          {/* Absent for the three built-in headings, which cannot be deleted — see the note
              on `remove`. Not disabled: a control that can never work is noise. It is worded
              here rather than drawn as a bare ✕, because on its own row an unlabelled icon
              has nothing beside it to say what it would remove. */}
          {manage.self.remove !== undefined && (
            <button
              type="button"
              className="sheet-title-delete"
              aria-label={`Delete the ${manage.self.noun} ${manage.self.label}`}
              disabled={manageBusy}
              onClick={() => {
                const self = manage.self;
                if (self?.remove === undefined) return;
                const remove = self.remove;
                void (async () => {
                  // The radius here is the DESTRUCTIVE one — stored values, not suggestions.
                  if (
                    !(await ask({
                      title: `Delete the ${self.noun} “${self.label}”?`,
                      body: `${self.radius ?? 'This cannot be undone from the app.'}. Items themselves are kept.`,
                      confirmLabel: `Delete ${self.noun}`,
                    }))
                  )
                    return;
                  void runManage(() => remove());
                })();
              }}
            >
              <Icon name="cross" /> Delete {manage.self.noun}
            </button>
          )}
        </p>
      )}
      <input
        ref={input}
        type="search"
        aria-label={`Search ${title}`}
        placeholder="Type to filter…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
        }}
        // Enter/Go on a phone keyboard is the natural end of typing a value, and it
        // used to do NOTHING — on a new field with zero options that read as "cannot
        // add options" (field report, 27 Jul; the Use-button can hide under the
        // keyboard). Enter now takes the obvious choice: the free value if offered,
        // else the single remaining match.
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (offerFree) chooseFree(freeValue);
          else if (shown.length === 1 && shown[0] !== undefined) {
            if (multi) toggle(shown[0]);
            else chooseSingle(shown[0]);
          }
        }}
      />
      {manageProblem !== null && <p role="alert">{manageProblem}</p>}
      {/* `overscroll-behavior: contain` in the stylesheet stops a scroll that reaches the
          end of this list from continuing on the page behind it — the "sometimes when I
          scroll in a dropdown, it scrolls the page behind it" report. */}
      <ul className="sheet-options">
        {/* THE ADD ROW SITS AT THE TOP (maintainer, 31 Jul 2026: "the add button should open
            the add field at the top of the list, not the bottom"). Categories run to several
            dozen and fields to a dozen; at the bottom the row opened below the fold, so
            pressing ＋ looked like it had done nothing at all. */}
        {manage !== undefined && addingNew !== null && (
          <li className="sheet-add-row">
            {addingNew !== null && (
              <>
                <input
                  className="sheet-rename"
                  type="text"
                  aria-label={`New ${manage.noun}`}
                  value={addingNew}
                  onChange={(e) => {
                    setAddingNew(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const name = addingNew.trim();
                    if (name === '') return;
                    // Cleared but kept open: adding one usually means adding several.
                    void runManage(() => runAdd(name)).then((ok) => {
                      if (ok) {
                        setAddingNew('');
                        setAddingExtra('');
                      }
                    });
                  }}
                />
                {/* The parent, when the list HAS parents. A native select rather than a
                    second nested dropdown: this is already inside a popover, and a popover
                    inside a popover is a thing to open, not a thing to use. It also keeps
                    the whole hierarchy reachable on a phone with no extra layout. */}
                {manage.extraNumber !== undefined && (
                  <input
                    className="sheet-extra"
                    type="number"
                    min={1}
                    max={1825}
                    inputMode="numeric"
                    aria-label={manage.extraNumber.label}
                    placeholder={manage.extraNumber.placeholder ?? manage.extraNumber.label}
                    value={addingExtra}
                    onChange={(e) => setAddingExtra(e.target.value)}
                  />
                )}
                {manage.parentOptions !== undefined && (
                  <select
                    className="sheet-parent"
                    aria-label={`Parent for the new ${manage.noun}`}
                    value={addParent}
                    onChange={(e) => {
                      setAddParent(e.target.value);
                    }}
                  >
                    <option value="">(top level)</option>
                    {manage.parentOptions.map((parent) => (
                      <option key={parent} value={parent}>
                        {parent}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  disabled={manageBusy || addingNew.trim() === ''}
                  onClick={() => {
                    void runManage(() => runAdd(addingNew.trim())).then((ok) => {
                      if (ok) setAddingNew('');
                    });
                  }}
                >
                  Add
                </button>
                <button type="button" onClick={() => { setAddingNew(null); }}>
                  Cancel
                </button>
              </>
            )}
          </li>
        )}
        {/* Single mode needs an explicit way OUT of a choice (field report, 27 Jul:
            "once i have chosen there is no way to undo") — multi mode toggles off,
            single mode closes on pick, so the un-pick must be its own row. */}
        {!multi && selected.length > 0 && (
          <li>
            <button
              type="button"
              className="sheet-option sheet-option-clear"
              onClick={() => {
                onChange([]);
                onClose();
              }}
            >
              Clear {title}
            </button>
          </li>
        )}
        {offerFree && (
          <li>
            <button type="button" className="sheet-option" onClick={() => chooseFree(freeValue)}>
              Use “{freeValue}”
            </button>
          </li>
        )}
        {shown.map((option) => (
          <li key={option} className={manage === undefined ? undefined : 'sheet-row'}>
            {manage !== undefined && editingAll ? (
              /**
               * EDIT MODE turns EVERY row into a text field at once — the pencil in the
               * header is a mode, not a per-row action (maintainer, 30 Jul 2026).
               *
               * Saving stays per-row on purpose. A single bulk save would have to decide
               * what to show when the third rename is refused and the first two are already
               * written, and "some of them worked" is not a state this list can report
               * honestly. One row, one result, one message.
               */
              <>
                <input
                  className="sheet-rename"
                  type="text"
                  aria-label={`New name for ${option}`}
                  value={drafts[option] ?? option}
                  onChange={(e) => {
                    setDrafts((prev) => ({ ...prev, [option]: e.target.value }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const next = (drafts[option] ?? option).trim();
                    if (next !== '') void runManage(() => runRename(option, next));
                  }}
                />
                {/* The extra field, editable in place — a source's standard return window
                    (5 Aug 2026: "the pencil button should allow me to rename sources in the
                    menu and edit their max return days"). Prefilled from `valueFor`, and an
                    empty box saves as "no standard", which is a real answer. */}
                {manage.extraNumber !== undefined && (
                  <input
                    className="sheet-extra"
                    type="number"
                    min={1}
                    max={1825}
                    inputMode="numeric"
                    aria-label={`${manage.extraNumber.label} for ${option}`}
                    placeholder={manage.extraNumber.placeholder ?? manage.extraNumber.label}
                    value={extraDrafts[option] ?? String(manage.extraNumber.valueFor?.(option) ?? '')}
                    onChange={(e) => {
                      setExtraDrafts((prev) => ({ ...prev, [option]: e.target.value }));
                    }}
                  />
                )}
                <button
                  type="button"
                  /* ⚠️ NOT disabled on "the name is unchanged" any more: with a second field
                     in the row, the name staying put while the number moves is an ordinary
                     edit, and the old guard made it unsaveable. */
                  disabled={manageBusy || (drafts[option] ?? option).trim() === ''}
                  onClick={() => {
                    const next = (drafts[option] ?? option).trim();
                    if (next !== '') void runManage(() => runRename(option, next));
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="sheet-row-delete"
                  aria-label={`Delete the ${manage.noun} ${option}`}
                  disabled={manageBusy}
                  onClick={() => {
                    void (async () => {
                      // Names the target AND the blast radius, even in edit mode: the
                      // database blanks this field on every item using it, silently.
                      if (
                        !(await ask({
                          title: `Delete the ${manage.noun} “${option}”?`,
                          body:
                            manage.deleteBodyFor !== undefined
                              ? manage.deleteBodyFor(option)
                              : `${manage.radiusFor(option)}. This cannot be undone from the app.`,
                          confirmLabel: `Delete ${manage.noun}`,
                          // Said, not defaulted: a delete is the destructive tone (`useConfirm`'s
                          // default is also 'danger'; naming it keeps a later default change honest).
                          tone: 'danger',
                        }))
                      )
                        return;
                      void runManage(() => manage.remove(option));
                    })();
                  }}
                >
                  <Icon name="cross" />
                </button>
              </>
            ) : (
              <button
                type="button"
                className={`sheet-option${(multi ? picked : selected).includes(option) ? ' sheet-option-on' : ''}`}
                // aria-pressed only in multi mode: single-select closes on tap, so
                // pressed-state semantics would be announced and immediately gone.
                {...(multi ? { 'aria-pressed': picked.includes(option) } : {})}
                onClick={() => {
                  if (multi) toggle(option);
                  else chooseSingle(option);
                }}
              >
                {multi && (
                  <span
                    className={`sheet-check${picked.includes(option) ? ' sheet-check-on' : ''}`}
                    aria-hidden="true"
                  />
                )}
                {/* §9 item 8.10 — a swatch beside a colour, and ONLY when the value is
                    one this app actually knows. An unrecognised word gets nothing at
                    all rather than a grey square, which would be indistinguishable from
                    the answer for "grijs" (see platform/colour.ts). aria-hidden because
                    the option's own text is already its accessible name — a swatch that
                    announced itself would read every colour twice. */}
                {colourField &&
                  (() => {
                    const hex = swatchFor(option);
                    return hex === null ? null : (
                      <span
                        className={needsOutline(hex) ? 'sheet-swatch sheet-swatch-pale' : 'sheet-swatch'}
                        style={{ background: hex }}
                        aria-hidden="true"
                      />
                    );
                  })()}
                {option}
              </button>
            )}
          </li>
        ))}
        {shown.length === 0 && !offerFree && <li className="sheet-empty">Nothing matches.</li>}
        {/* THE FIELD ITSELF used to be a row here, below the options. It moved into the
            header on 1 Aug 2026 — see the note there. Nothing replaces it: the name of a
            thing belongs where the name of that thing already is. */}
      </ul>
      {footer !== undefined && <div className="sheet-below-options">{footer}</div>}
      {multi ? (
        <footer className="sheet-footer">
          {/* ⚠️ MULTI KEEPS `Done`, AGAINST THE REDESIGN'S BLANKET RULE, on purpose: this
              mode genuinely commits the whole set at once — the jobs sheet's own note says
              firing per toggle would write a half-made answer. Relabelling the commit
              `Close` would claim the picks were already saved when they are not. */}
          <button
            type="button"
            onClick={() => {
              onChange(picked);
              onClose();
            }}
          >
            Done
          </button>
        </footer>
      ) : (
        <footer className="sheet-footer sheet-footer-live">
          {/* Redesign 5b, and here it is TRUE: single mode writes on the tap itself, so
              the footer says so and the only button is a dismissal — unfilled, `Close`,
              never `Done`, because there is nothing left to do. */}
          <small>Saved as you pick</small>
          <button type="button" className="chip" onClick={onClose}>
            Close
          </button>
        </footer>
      )}
      </div>
    </>
  );
}
